import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatRelativeTime } from '../../lib/utils';
import {
  Search,
  Loader2,
  Trash2,
  Eye,
  EyeOff,
  Star,
  StarOff,
  Crown,
  ChevronLeft,
  ChevronRight,
  Download,
  CheckCircle,
  Store,
  Settings,
  Users,
  Pencil,
  ExternalLink,
  Save,
  Link2,
  Unlink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  draft: 'bg-muted text-muted-foreground',
  pending_review: 'bg-warning/20 text-warning',
  published: 'bg-success/20 text-success',
  hidden: 'bg-danger/20 text-danger',
};

const CATEGORIES = [
  { value: 'campaign', key: 'marketplace_category_campaign' },
  { value: 'one-shot', key: 'marketplace_category_oneshot' },
  { value: 'module', key: 'marketplace_category_module' },
  { value: 'adventure', key: 'marketplace_category_adventure' },
];

const DIFFICULTIES = [
  { value: 'beginner', key: 'marketplace_difficulty_beginner' },
  { value: 'intermediate', key: 'marketplace_difficulty_intermediate' },
  { value: 'advanced', key: 'marketplace_difficulty_advanced' },
  { value: 'expert', key: 'marketplace_difficulty_expert' },
];

const STATUSES = [
  { value: 'draft', key: 'marketplace_status_draft' },
  { value: 'pending_review', key: 'marketplace_status_pending' },
  { value: 'published', key: 'marketplace_status_published' },
  { value: 'hidden', key: 'marketplace_status_hidden' },
];

const CONTENT_LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'pl', label: 'Polski' },
  { value: 'ru', label: 'Русский' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh', label: '中文' },
];

export default function AdminMarketplacePage() {
  const { language } = useAuth();

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <Store className="h-6 w-6 text-primary" />
          <h1 className="font-cinzel text-2xl font-bold">{t('admin_marketplace', language)}</h1>
        </div>

        <Tabs defaultValue="templates">
          <TabsList>
            <TabsTrigger value="templates">{t('admin_marketplace_templates', language)}</TabsTrigger>
            <TabsTrigger value="settings">{t('admin_marketplace_settings', language)}</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-4">
            <TemplatesTab language={language} />
          </TabsContent>
          <TabsContent value="settings" className="mt-4">
            <SettingsTab language={language} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

// ========== Edit Template Dialog ==========
function EditTemplateDialog({ template, open, onOpenChange, onSaved, language }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    short_description: '',
    long_description: '',
    universe: '',
    game_system: '',
    category: 'campaign',
    difficulty: 'intermediate',
    tags: '',
    cover_image_url: '',
    language: 'fr',
    status: 'draft',
    is_featured: false,
    is_official: false,
  });

  // Populate form when template changes
  useEffect(() => {
    if (template && open) {
      setForm({
        name: template.name || '',
        short_description: template.short_description || '',
        long_description: template.long_description || '',
        universe: template.universe || '',
        game_system: template.game_system || '',
        category: template.category || 'campaign',
        difficulty: template.difficulty || 'intermediate',
        tags: (template.tags || []).join(', '),
        cover_image_url: template.cover_image_url || '',
        language: template.language || 'fr',
        status: template.status || 'draft',
        is_featured: !!template.is_featured,
        is_official: !!template.is_official,
      });
    }
  }, [template, open]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!template) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        short_description: form.short_description,
        long_description: form.long_description,
        universe: form.universe,
        game_system: form.game_system,
        category: form.category,
        difficulty: form.difficulty,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        cover_image_url: form.cover_image_url,
        language: form.language,
        status: form.status,
        is_featured: form.is_featured,
        is_official: form.is_official,
      };

      await axios.patch(`/api/admin/marketplace/templates/${template.id}`, payload);
      toast.success(language === 'fr' ? 'Template mis à jour avec succès' : 'Template updated successfully');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              {language === 'fr' ? 'Modifier le template' : 'Edit Template'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Modifiez toutes les informations du template marketplace.'
                : 'Edit all marketplace template information.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* ── Section : Informations principales ── */}
            <div className="space-y-1">
              <h3 className="font-semibold text-sm text-primary">
                {language === 'fr' ? 'Informations principales' : 'Main information'}
              </h3>
              <div className="h-px bg-border" />
            </div>

            {/* Nom */}
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('marketplace_name', language)} *</Label>
              <Input
                id="edit-name"
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                required
              />
            </div>

            {/* Description courte */}
            <div className="space-y-2">
              <Label htmlFor="edit-short">{t('marketplace_short_desc', language)}</Label>
              <Input
                id="edit-short"
                value={form.short_description}
                onChange={e => updateField('short_description', e.target.value)}
                maxLength={300}
                placeholder={language === 'fr' ? 'Résumé en une ou deux phrases' : 'One or two sentence summary'}
              />
              <p className="text-xs text-muted-foreground">{form.short_description.length}/300</p>
            </div>

            {/* Description longue */}
            <div className="space-y-2">
              <Label htmlFor="edit-long">{t('marketplace_long_desc', language)}</Label>
              <Textarea
                id="edit-long"
                value={form.long_description}
                onChange={e => updateField('long_description', e.target.value)}
                rows={6}
                placeholder={language === 'fr' ? 'Description détaillée du template...' : 'Detailed template description...'}
              />
            </div>

            {/* ── Section : Univers & Système ── */}
            <div className="space-y-1 pt-2">
              <h3 className="font-semibold text-sm text-primary">
                {language === 'fr' ? 'Univers & Système' : 'Universe & System'}
              </h3>
              <div className="h-px bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Univers */}
              <div className="space-y-2">
                <Label htmlFor="edit-universe">{t('marketplace_universe', language)}</Label>
                <Input
                  id="edit-universe"
                  value={form.universe}
                  onChange={e => updateField('universe', e.target.value)}
                  placeholder="D&D 5e, Pathfinder, ..."
                />
              </div>

              {/* Système de jeu */}
              <div className="space-y-2">
                <Label htmlFor="edit-system">{t('marketplace_game_system', language)}</Label>
                <Input
                  id="edit-system"
                  value={form.game_system}
                  onChange={e => updateField('game_system', e.target.value)}
                  placeholder="D&D 5e, PF2e, ..."
                />
              </div>
            </div>

            {/* ── Section : Classification ── */}
            <div className="space-y-1 pt-2">
              <h3 className="font-semibold text-sm text-primary">
                {language === 'fr' ? 'Classification' : 'Classification'}
              </h3>
              <div className="h-px bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Catégorie */}
              <div className="space-y-2">
                <Label>{t('marketplace_category', language)}</Label>
                <Select value={form.category} onValueChange={v => updateField('category', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{t(c.key, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Difficulté */}
              <div className="space-y-2">
                <Label>{t('marketplace_difficulty', language)}</Label>
                <Select value={form.difficulty} onValueChange={v => updateField('difficulty', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{t(d.key, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Langue */}
              <div className="space-y-2">
                <Label>{t('marketplace_language', language)}</Label>
                <Select value={form.language} onValueChange={v => updateField('language', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_LANGUAGES.map(l => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="edit-tags">{t('marketplace_tags', language)}</Label>
              <Input
                id="edit-tags"
                value={form.tags}
                onChange={e => updateField('tags', e.target.value)}
                placeholder={language === 'fr' ? 'fantasy, médiéval, enquête, horreur' : 'fantasy, medieval, mystery, horror'}
              />
              <p className="text-xs text-muted-foreground">
                {language === 'fr' ? 'Séparez les tags par des virgules' : 'Separate tags with commas'}
              </p>
            </div>

            {/* ── Section : Visuel ── */}
            <div className="space-y-1 pt-2">
              <h3 className="font-semibold text-sm text-primary">
                {language === 'fr' ? 'Visuel' : 'Visual'}
              </h3>
              <div className="h-px bg-border" />
            </div>

            {/* Image de couverture */}
            <div className="space-y-2">
              <Label htmlFor="edit-cover">{t('marketplace_cover_url', language)}</Label>
              <Input
                id="edit-cover"
                value={form.cover_image_url}
                onChange={e => updateField('cover_image_url', e.target.value)}
                placeholder="https://..."
              />
              {form.cover_image_url && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border h-32 w-full">
                  <img
                    src={form.cover_image_url}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            {/* ── Section : Statut & Options ── */}
            <div className="space-y-1 pt-2">
              <h3 className="font-semibold text-sm text-primary">
                {language === 'fr' ? 'Statut & Options' : 'Status & Options'}
              </h3>
              <div className="h-px bg-border" />
            </div>

            {/* Statut */}
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Statut de publication' : 'Publication status'}</Label>
              <Select value={form.status} onValueChange={v => updateField('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{t(s.key, language)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Toggles */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-accent" />
                  <div>
                    <div className="text-sm font-medium">
                      {t('admin_marketplace_featured', language)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {language === 'fr'
                        ? 'Affiché en vedette sur la page du marketplace'
                        : 'Displayed prominently on the marketplace page'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={form.is_featured}
                  onCheckedChange={v => updateField('is_featured', v)}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-primary" />
                  <div>
                    <div className="text-sm font-medium">
                      {t('admin_marketplace_official', language)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {language === 'fr'
                        ? 'Marqué comme template officiel avec badge'
                        : 'Marked as official template with badge'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={form.is_official}
                  onCheckedChange={v => updateField('is_official', v)}
                />
              </div>
            </div>

            {/* ── Section : Métadonnées (lecture seule) ── */}
            {template && (
              <>
                <div className="space-y-1 pt-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">
                    {language === 'fr' ? 'Métadonnées' : 'Metadata'}
                  </h3>
                  <div className="h-px bg-border" />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium">{language === 'fr' ? 'Auteur' : 'Author'}:</span>{' '}
                    {template.author_name || '—'}
                  </div>
                  <div>
                    <span className="font-medium">{language === 'fr' ? 'Imports' : 'Imports'}:</span>{' '}
                    {template.import_count || 0}
                  </div>
                  <div>
                    <span className="font-medium">{language === 'fr' ? 'Créé le' : 'Created'}:</span>{' '}
                    {template.created_at ? new Date(template.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : '—'}
                  </div>
                  <div>
                    <span className="font-medium">{language === 'fr' ? 'Publié le' : 'Published'}:</span>{' '}
                    {template.published_at ? new Date(template.published_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : '—'}
                  </div>
                  {template.included_content && (
                    <div className="col-span-2">
                      <span className="font-medium">{t('marketplace_included_content', language)}:</span>{' '}
                      {Object.entries(template.included_content)
                        .filter(([, v]) => v > 0)
                        .map(([k, v]) => `${v} ${k}`)
                        .join(', ') || '—'}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel', language)}
            </Button>
            <Button type="submit" disabled={saving} className="fantasy-button">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {language === 'fr' ? 'Sauvegarde...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('save', language)}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ========== Templates Tab ==========
function TemplatesTab({ language }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [saveConfirm, setSaveConfirm] = useState(null);

  const LIMIT = 15;

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * LIMIT, limit: LIMIT };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get('/api/admin/marketplace/templates', { params });
      setTemplates(res.data.templates);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading templates');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleAction = async (templateId, updates, actionName) => {
    setActionLoading(templateId + actionName);
    try {
      await axios.patch(`/api/admin/marketplace/templates/${templateId}`, updates);
      toast.success(language === 'fr' ? 'Template mis à jour' : 'Template updated');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleDelete = async (templateId) => {
    setActionLoading(templateId + 'delete');
    try {
      await axios.delete(`/api/admin/marketplace/templates/${templateId}`);
      toast.success(language === 'fr' ? 'Template supprimé' : 'Template deleted');
      setDeleteConfirm(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleOpenCampaign = async (templateId) => {
    setActionLoading(templateId + 'open');
    try {
      const res = await axios.post(`/api/admin/marketplace/templates/${templateId}/open-campaign`);
      const { campaign_id, already_existed } = res.data;
      toast.success(
        already_existed
          ? t('admin_marketplace_campaign_exists', language)
          : t('admin_marketplace_open_success', language)
      );
      fetchTemplates();
      navigate(`/campaigns/${campaign_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'ouverture' : 'Error opening campaign'));
    } finally {
      setActionLoading('');
    }
  };

  const handleSaveToCampaign = async (templateId) => {
    setActionLoading(templateId + 'save');
    try {
      await axios.post(`/api/admin/marketplace/templates/${templateId}/save-campaign`);
      toast.success(t('admin_marketplace_save_success', language));
      setSaveConfirm(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving campaign'));
    } finally {
      setActionLoading('');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t('marketplace_search_placeholder', language)}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary" size="icon">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('admin_marketplace_all_statuses', language)} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin_marketplace_all_statuses', language)}</SelectItem>
            <SelectItem value="draft">{t('marketplace_status_draft', language)}</SelectItem>
            <SelectItem value="pending_review">{t('marketplace_status_pending', language)}</SelectItem>
            <SelectItem value="published">{t('marketplace_status_published', language)}</SelectItem>
            <SelectItem value="hidden">{t('marketplace_status_hidden', language)}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="text-sm text-muted-foreground">
        {total} template{total !== 1 ? 's' : ''}
      </div>

      {/* Templates List */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Store className="h-10 w-10 mx-auto mb-3 opacity-40" />
            {t('admin_marketplace_no_templates', language)}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {templates.map(tmpl => (
            <Card key={tmpl.id} className="glass-card">
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-4">
                  {/* Cover thumbnail */}
                  <div className="hidden sm:block w-12 h-12 rounded bg-muted overflow-hidden shrink-0">
                    {tmpl.cover_image_url ? (
                      <img src={tmpl.cover_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Store className="h-5 w-5 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{tmpl.name}</span>
                      <span className={`status-chip text-[10px] ${STATUS_COLORS[tmpl.status]}`}>
                        {t(`marketplace_status_${tmpl.status}`, language)}
                      </span>
                      {tmpl.is_featured && (
                        <Star className="h-3.5 w-3.5 text-accent fill-accent" />
                      )}
                      {tmpl.is_official && (
                        <Crown className="h-3.5 w-3.5 text-primary" />
                      )}
                      {tmpl.admin_campaign_id && (
                        <span className="flex items-center gap-0.5 text-[10px] text-info">
                          <Link2 className="h-3 w-3" />
                          {language === 'fr' ? 'Campagne liée' : 'Linked'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>{tmpl.author_name}</span>
                      <span>{t(`marketplace_category_${tmpl.category}`, language)}</span>
                      <span>{t(`marketplace_difficulty_${tmpl.difficulty}`, language)}</span>
                      {tmpl.language && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-semibold uppercase">
                          {tmpl.language}
                        </Badge>
                      )}
                      {tmpl.universe && <span>{tmpl.universe}</span>}
                      <span className="flex items-center gap-0.5">
                        <Download className="h-3 w-3" />{tmpl.import_count || 0}
                      </span>
                      <span>{formatRelativeTime(tmpl.created_at, language)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Open campaign for editing */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('admin_marketplace_open_campaign', language)}
                      onClick={() => handleOpenCampaign(tmpl.id)}
                      disabled={!!actionLoading}
                    >
                      {actionLoading === tmpl.id + 'open' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4 text-info" />
                      )}
                    </Button>

                    {/* Save campaign back to template (only if linked campaign exists) */}
                    {tmpl.admin_campaign_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('admin_marketplace_save_to_template', language)}
                        onClick={() => setSaveConfirm(tmpl)}
                        disabled={!!actionLoading}
                      >
                        <Save className="h-4 w-4 text-success" />
                      </Button>
                    )}

                    {/* Edit metadata */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('edit', language)}
                      onClick={() => setEditTemplate(tmpl)}
                    >
                      <Pencil className="h-4 w-4 text-primary" />
                    </Button>

                    {/* Publish */}
                    {tmpl.status !== 'published' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('admin_marketplace_publish_template', language)}
                        onClick={() => handleAction(tmpl.id, { status: 'published' }, 'publish')}
                        disabled={!!actionLoading}
                      >
                        <CheckCircle className="h-4 w-4 text-success" />
                      </Button>
                    )}

                    {/* Hide */}
                    {tmpl.status === 'published' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('admin_marketplace_hide_template', language)}
                        onClick={() => handleAction(tmpl.id, { status: 'hidden' }, 'hide')}
                        disabled={!!actionLoading}
                      >
                        <EyeOff className="h-4 w-4 text-warning" />
                      </Button>
                    )}

                    {/* Feature */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title={tmpl.is_featured
                        ? (language === 'fr' ? 'Retirer la mise en avant' : 'Remove from featured')
                        : (language === 'fr' ? 'Mettre en avant' : 'Feature')}
                      onClick={() => handleAction(tmpl.id, { is_featured: !tmpl.is_featured }, 'feature')}
                      disabled={!!actionLoading}
                    >
                      {tmpl.is_featured ? (
                        <StarOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Star className="h-4 w-4 text-accent" />
                      )}
                    </Button>

                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('delete', language)}
                      onClick={() => setDeleteConfirm(tmpl)}
                      disabled={!!actionLoading}
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="icon" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">{page + 1} / {totalPages}</span>
          <Button variant="outline" size="icon" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <EditTemplateDialog
        template={editTemplate}
        open={!!editTemplate}
        onOpenChange={(open) => { if (!open) setEditTemplate(null); }}
        onSaved={fetchTemplates}
        language={language}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-cinzel">{t('delete', language)}</DialogTitle>
            <DialogDescription>
              {t('admin_marketplace_confirm_delete', language)}
              {deleteConfirm && <><br /><strong>{deleteConfirm.name}</strong></>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>{t('cancel', language)}</Button>
            <Button
              variant="destructive"
              disabled={actionLoading === (deleteConfirm?.id + 'delete')}
              onClick={() => deleteConfirm && handleDelete(deleteConfirm.id)}
            >
              {actionLoading === (deleteConfirm?.id + 'delete') && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('delete', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save to Template Confirmation */}
      <Dialog open={!!saveConfirm} onOpenChange={() => setSaveConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2">
              <Save className="h-5 w-5 text-success" />
              {t('admin_marketplace_save_to_template', language)}
            </DialogTitle>
            <DialogDescription>
              {t('admin_marketplace_save_to_template_desc', language)}
              {saveConfirm && <><br /><br /><strong>{saveConfirm.name}</strong></>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveConfirm(null)}>{t('cancel', language)}</Button>
            <Button
              className="fantasy-button"
              disabled={actionLoading === (saveConfirm?.id + 'save')}
              onClick={() => saveConfirm && handleSaveToCampaign(saveConfirm.id)}
            >
              {actionLoading === (saveConfirm?.id + 'save') ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {t('admin_marketplace_save_to_template', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== Settings Tab ==========
function SettingsTab({ language }) {
  const [mode, setMode] = useState('admin_only');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [togglingUser, setTogglingUser] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/admin/marketplace/settings');
        setMode(res.data.marketplace_publish_mode || 'admin_only');
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (mode === 'per_user') {
      fetchUsers();
    }
  }, [mode]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await axios.get('/api/admin/users', { params: { skip: 0, limit: 200 } });
      setUsers(res.data.users || res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSaveMode = async () => {
    setSaving(true);
    try {
      await axios.patch('/api/admin/marketplace/settings', { marketplace_publish_mode: mode });
      toast.success(t('admin_marketplace_settings_saved', language));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUser = async (userId, currentValue) => {
    setTogglingUser(userId);
    try {
      await axios.patch(`/api/admin/users/${userId}/marketplace-permissions`, {
        can_publish_templates: !currentValue,
      });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, can_publish_templates: !currentValue } : u
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setTogglingUser('');
    }
  };

  if (loading) {
    return <div className="h-32 bg-muted animate-pulse rounded-lg" />;
  }

  const MODES = [
    { value: 'admin_only', key: 'admin_marketplace_mode_admin_only', descKey: 'admin_marketplace_mode_admin_only_desc' },
    { value: 'global', key: 'admin_marketplace_mode_global', descKey: 'admin_marketplace_mode_global_desc' },
    { value: 'per_user', key: 'admin_marketplace_mode_per_user', descKey: 'admin_marketplace_mode_per_user_desc' },
  ];

  const filteredUsers = users.filter(u =>
    !u.is_admin && (
      !userSearch ||
      u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase())
    )
  );

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Publish Mode */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-cinzel text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t('admin_marketplace_publish_mode', language)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {MODES.map(m => (
            <label
              key={m.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mode === m.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
              }`}
            >
              <input
                type="radio"
                name="publish_mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => setMode(m.value)}
                className="mt-1 accent-primary"
              />
              <div>
                <div className="font-medium text-sm">{t(m.key, language)}</div>
                <div className="text-xs text-muted-foreground">{t(m.descKey, language)}</div>
              </div>
            </label>
          ))}

          <Button onClick={handleSaveMode} disabled={saving} className="w-full">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('admin_marketplace_save_settings', language)}
          </Button>
        </CardContent>
      </Card>

      {/* Per-User Permissions */}
      {mode === 'per_user' && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-cinzel text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('admin_marketplace_user_permissions', language)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={language === 'fr' ? 'Rechercher un utilisateur...' : 'Search users...'}
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
            />

            {usersLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 bg-muted animate-pulse rounded" />
                ))}
              </div>
            ) : (
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {filteredUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-2 rounded hover:bg-secondary/50">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{user.username}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">
                        {t('admin_marketplace_can_publish', language)}
                      </span>
                      <Switch
                        checked={!!user.can_publish_templates}
                        onCheckedChange={() => handleToggleUser(user.id, !!user.can_publish_templates)}
                        disabled={togglingUser === user.id}
                      />
                    </div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="text-center text-muted-foreground text-sm py-4">
                    {language === 'fr' ? 'Aucun utilisateur trouvé' : 'No users found'}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
